// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';
import React, { Fragment, useState, useEffect, useMemo } from 'react'
import {Transition, Combobox } from '@headlessui/react'
import { Plus, ChevronDown, Tag, CreditCard, User, Check, Calendar } from 'lucide-react'
import { useTransactionStore } from '@/store/transactionsStore'
import { useAccountsStore } from '@/store/accountsStore'
import { useCategoriesStore } from '@/store/categoriesStore'
import { useCurrenciesStore } from '@/store/currenciesStore'
import { usePayeesStore } from '@/store/payeesStore'
import { useTagsStore } from '@/store/tagsStore'
import { useAuthStore } from '@/store/authStore'
import { format, parseISO } from 'date-fns'
// Migrated: Using native Date instead of Firebase Timestamp
import { toast } from "sonner";
import { AttachmentUpload } from './AttachmentUpload'
import { Transaction, Attachment, TransactionType, TransactionStatus } from '@/types/transaction'
import { useTransactionStore as useCoreTransactionStore, useCurrencyStore } from '@flows.cash/core'
import { useQueryClient } from '@tanstack/react-query'
import { invalidateTransactions } from '@/utils/queryUtils'
import { TagSelect } from './TagSelect'
import { ExchangeRateInput } from './ExchangeRateInput'
import { deleteTransactionAttachment } from '@/utils/fileUpload'
import { TransferEditModal } from '@/components/transfers/TransferEditModal'
import { findPotentialTransferMatches, PotentialTransferMatch } from './utils/transferDetection'
import { TransferMergeModal } from './TransferMergeModal'
import { StyledModal, StyledModalButton } from '@/components/ui/styled-modal'

// Currency symbol mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  TRY: '₺',
  INR: '₹',
  CNY: '¥',
  RUB: '₽',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'Fr',
  SEK: 'kr',
  NOK: 'kr',
  PLN: 'zł',
  BRL: 'R$',
  MXN: '$',
  ZAR: 'R',
  KRW: '₩',
  SGD: 'S$',
  HKD: 'HK$',
  NZD: 'NZ$',
  // Cryptocurrencies
  BTC: '₿',
  ETH: 'Ξ',
  USDT: '₮',
  // Precious Metals
  XAU: 'Au',
  XAG: 'Ag',
  XPT: 'Pt',
  XPD: 'Pd',
  XRH: 'Rh'
};

// Helper function to get currency symbol from account
const getCurrencySymbol = (accounts: any[], accountId: string): string => {
  if (!accountId || !accounts.length) return '$';
  
  const account = accounts.find(acc => acc.id === accountId);
  if (!account?.currency) return '$';
  
  return CURRENCY_SYMBOLS[account.currency] || account.currency;
};



// Define types for date handling
type DateOrDate = Date | string;

// Define Timestamp interface for Firebase compatibility
interface Timestamp {
  toDate: () => Date;
  seconds: number;
  nanoseconds: number;
}

interface MainCategory {
  id: string;
  name: string;
  type: string;
  color: string;
  subcategories?: {
    id: string;
    name: string;
  }[];
}

interface FormPayee {
  id: string;
  name: string;
}

interface FormData {
  type: 'expense' | 'income' | 'transfer';
  amount: string;
  date: string;
  description: string;
  category: string;
  categoryName?: string;
  accountId: string;
  payee: FormPayee;
  notes: string;
  tags: string[];
  attachments: Attachment[];
  status: TransactionStatus;
  toAccountId: string;
  exchangeRate: number;
}

type Category = MainCategory;

function formatDate(date: DateOrDate | string | undefined): string {
  if (!date) return '';
  
  try {
    // Handle Firebase Timestamp objects
    if (typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
      const timestamp = date as unknown as Timestamp;
      return format(timestamp.toDate(), 'yyyy-MM-dd\'T\'HH:mm');
    }
    
    // Handle Date objects
    if (date instanceof Date) {
      return format(date, 'yyyy-MM-dd\'T\'HH:mm');
    }
    
    // Handle ISO strings
    if (typeof date === 'string') {
      return format(parseISO(date), 'yyyy-MM-dd\'T\'HH:mm');
    }
    
    return '';
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

interface EditTransactionProps {
  isOpen: boolean;
  onClose: () => void;
  currentTransaction: Transaction;
}



export default function EditTransaction({ isOpen, onClose, currentTransaction }: EditTransactionProps) {
  // @ts-ignore - Type conversion issues during migration
  const { user } = useAuthStore();
  const { accounts } = useAccountsStore();
  const { categories } = useCategoriesStore();
  const { payees, addPayee, fetchPayees } = usePayeesStore();
  const { tags, addTag, fetchTags } = useTagsStore();
  const coreTransactionStore = useCoreTransactionStore();
  const transactionStore = useTransactionStore();
  const { convertAmount: coreConvertAmount } = useCurrencyStore();
  const queryClient = useQueryClient();
  
  // Get transactions for potential match detection
  const { transactions } = transactionStore;
  
  // Ensure payees and tags are loaded when the component mounts
  useEffect(() => {
    if (user?.id && isOpen) {
      fetchPayees(user.id);
      fetchTags(user.id);
    }
  }, [user?.id, isOpen, fetchPayees, fetchTags]);
  
  const [formData, setFormData] = useState<FormData>(() => {
    if (!currentTransaction) {
      return {
        type: 'expense',
        amount: '',
        date: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
        description: '',
        category: '',
        accountId: '',
        payee: { id: '', name: '' },
        notes: '',
        tags: [],
        attachments: [],
        status: 'completed',
        toAccountId: '',
        exchangeRate: 1
      };
    }
    
    // Initialize form data from the current transaction
    return {
      type: currentTransaction.type as 'expense' | 'income' | 'transfer',
      amount: currentTransaction.amount.toString(),
      date: formatDate(currentTransaction.date),
      description: currentTransaction.description || '',
      category: currentTransaction.category_id || '',
      categoryName: currentTransaction.metadata?.category || '',
      accountId: currentTransaction.account_id || '',
      payee: { 
        id: currentTransaction.payee_id || '', 
        name: currentTransaction.payee_name || '' 
      },
      notes: currentTransaction.notes || '',
      tags: currentTransaction.tags || [],
      attachments: currentTransaction.metadata?.attachments || [],
      status: currentTransaction.status || 'completed',
      toAccountId: currentTransaction.to_account_id || '',
      exchangeRate: currentTransaction.exchange_rate || 1
    };
  });

  // Add an effect to update formData when currentTransaction changes
  useEffect(() => {
    if (currentTransaction?.id && isOpen) {
      setFormData(prev => ({
        ...prev,
        type: currentTransaction.type as 'expense' | 'income' | 'transfer',
        amount: currentTransaction.amount.toString(),
        date: formatDate(currentTransaction.date),
        description: currentTransaction.description || '',
        category: currentTransaction.category_id || '',
        categoryName: currentTransaction.metadata?.category || '',
        accountId: currentTransaction.account_id || '',
        payee: { 
          id: currentTransaction.payee_id || '', 
          name: currentTransaction.payee_name || '' 
        },
        notes: currentTransaction.notes || '',
        tags: currentTransaction.tags || [],
        attachments: currentTransaction.metadata?.attachments || [],
        status: currentTransaction.status || 'completed',
        toAccountId: currentTransaction.to_account_id || '',
        exchangeRate: currentTransaction.exchange_rate || 1
      }));
    }
  }, [currentTransaction?.id, currentTransaction?.date, isOpen]);

  // Add a separate effect to handle attachments
  useEffect(() => {
    if (currentTransaction?.metadata?.attachments) {
      setFormData(prev => ({
        ...prev,
        attachments: [...(currentTransaction.metadata?.attachments || [])]
      }));
    }
  }, [currentTransaction?.metadata?.attachments]);

  // Add this useEffect to set the payee query when the modal opens
  useEffect(() => {
    if (isOpen && formData.payee?.name) {
      setPayeeQuery(formData.payee.name);
    }
  }, [isOpen, formData.payee?.name]);

  // Add this useEffect after the useState declarations
  useEffect(() => {
    if (!currentTransaction?.id && isOpen) {
      onClose?.();
    }
  }, [currentTransaction?.id, isOpen, onClose]);

  const [type, setType] = useState<'expense' | 'income' | 'transfer'>(formData.type);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddPayeeModalOpen, setIsAddPayeeModalOpen] = useState(false);
  const [newPayeeName, setNewPayeeName] = useState('');
  const [isAddingPayee, setIsAddingPayee] = useState(false);
  const [isAddTagModalOpen, setIsAddTagModalOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#6366F1');
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [categoryQuery, setCategoryQuery] = useState('');
  const [payeeQuery, setPayeeQuery] = useState('');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isPayeeOpen, setIsPayeeOpen] = useState(false);
  
  // Transfer merge detection state
  const [showTransferMergeModal, setShowTransferMergeModal] = useState(false);
  const [potentialMatches, setPotentialMatches] = useState<PotentialTransferMatch[]>([]);

  // Update type state when formData.type changes
  useEffect(() => {
    if (formData.type !== type) {
      handleInputChange('type', type);
    }
  }, [type]);

  // Close other dropdowns when one opens
  const handleCategoryOpen = () => {
    setIsCategoryOpen(true);
    setIsPayeeOpen(false);
  };

  const handlePayeeOpen = () => {
    setIsPayeeOpen(true);
    setIsCategoryOpen(false);
  };

  // Reset queries when dropdowns close
  useEffect(() => {
    if (!isCategoryOpen) setCategoryQuery('');
  }, [isCategoryOpen]);

  useEffect(() => {
    if (!isPayeeOpen) setPayeeQuery('');
  }, [isPayeeOpen]);

  // Handle outside clicks for payee dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.payee-dropdown-container')) {
        setIsPayeeOpen(false);
      }
    };

    if (isPayeeOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isPayeeOpen]);

  // Auto-fetch exchange rates when accounts change for transfers
  useEffect(() => {
    const autoFetchExchangeRate = async () => {
      if (formData.type !== 'transfer' || !formData.accountId || !formData.toAccountId) {
        return;
      }

      const fromAccount = accounts.find((acc: any) => acc.id === formData.accountId);
      const toAccount = accounts.find((acc: any) => acc.id === formData.toAccountId);
      
      if (!fromAccount?.currency || !toAccount?.currency || fromAccount.currency === toAccount.currency) {
        // Same currency, set rate to 1
        setFormData(prev => ({ ...prev, exchangeRate: 1 }));
        return;
      }

      try {
        let rate: number;
        try {
          rate = coreConvertAmount(1, fromAccount.currency, toAccount.currency);
        } catch (error) {
          console.error('Error getting exchange rate:', error);
          rate = 1; // Default to 1:1 rate if conversion fails
        }

        if (rate && !isNaN(rate)) {
          const roundedRate = Math.round(rate * 1000000) / 1000000; // 6 decimal places
          setFormData(prev => ({ ...prev, exchangeRate: roundedRate }));
        }
      } catch (error) {
        console.error('Error auto-fetching exchange rate:', error);
      }
    };

    autoFetchExchangeRate();
  }, [formData.type, formData.accountId, formData.toAccountId, accounts, coreConvertAmount]);

  // NEW: Re-run transfer detection when exchange rate changes for different currency transfers
  useEffect(() => {
    const triggerDetectionOnExchangeRateChange = async () => {
      if (formData.type !== 'transfer' || !formData.accountId || !formData.toAccountId || !currentTransaction) {
        return;
      }

      const fromAccount = accounts.find((acc: any) => acc.id === formData.accountId);
      const toAccount = accounts.find((acc: any) => acc.id === formData.toAccountId);
      
      // Only trigger for different currency transfers
      if (fromAccount?.currency && toAccount?.currency && fromAccount.currency !== toAccount.currency) {
        console.log('💱 Exchange rate changed for different currency transfer - triggering detection');
        setTimeout(() => {
          handleTransferTypeChangeWithAccount(formData.toAccountId);
        }, 300); // Small delay to allow rate to settle
      }
    };

    triggerDetectionOnExchangeRateChange();
  }, [formData.exchangeRate]); // Only trigger on exchange rate changes

  // Filter categories based on type and search query
  const filteredCategories = useMemo(() => {
    const typeFilter = (category: Category) => 
      category.type === formData.type;

    const searchFilter = (category: Category) => {
      const matchInCategory = category.name.toLowerCase().includes(categoryQuery.toLowerCase());
      const matchInSubcategories = category.subcategories?.some(
        sub => sub.name.toLowerCase().includes(categoryQuery.toLowerCase())
      );
      return matchInCategory || matchInSubcategories;
    };

    return categoryQuery === ''
      ? categories.filter(typeFilter)
      : categories.filter(category => typeFilter(category) && searchFilter(category));
  }, [categories, categoryQuery, formData.type]);

  const filteredPayees = payeeQuery === ''
    ? payees
    : payees.filter(payee =>
        payee.name.toLowerCase().includes(payeeQuery.toLowerCase())
      );

  // Helper function for transfer detection with specific target account ID (like mobile version)
  const triggerTransferDetectionWithAccount = (targetAccountId: string, reason: string) => {
    console.log(`Web: ${reason}, triggering detection with target account: ${targetAccountId}`);
    setTimeout(() => {
      handleTransferTypeChangeWithAccount(targetAccountId);
    }, 100);
  };

  // Helper function for transfer detection - moved outside to avoid hoisting issues
  const triggerTransferDetectionDelayed = (reason: string) => {
    console.log(`Web: ${reason}, triggering detection...`);
    setTimeout(() => {
      handleTransferTypeChange();
    }, 100);
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'tags' ? (Array.isArray(value) ? value : []) : value
    }))
    
    // If type is changing, we need to ensure it's tracked
    if (field === 'type') {
      setType(value as 'expense' | 'income' | 'transfer')
      
      // Trigger transfer detection when type changes to 'transfer'
      if (value === 'transfer') {
        triggerTransferDetectionDelayed('type changed to transfer');
      }
    }
    
    // Trigger transfer detection when toAccountId is selected for transfer transactions
    if (field === 'toAccountId' && formData.type === 'transfer' && value) {
      triggerTransferDetectionWithAccount(value, 'toAccountId selected for transfer');
    }
    
    // Also trigger transfer detection when accountId (from account) changes and toAccountId is already selected
    if (field === 'accountId' && formData.type === 'transfer' && formData.toAccountId && value) {
      triggerTransferDetectionWithAccount(formData.toAccountId, 'accountId changed for transfer with existing toAccountId');
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.amount || isNaN(parseFloat(formData.amount))) {
      newErrors.amount = 'Please enter a valid amount';
    }
    
    if (!formData.date) {
      newErrors.date = 'Please select a date';
    }
    
    if (!formData.accountId) {
      newErrors.accountId = 'Please select an account';
    }

    if (formData.type === 'transfer') {
      if (!formData.toAccountId) {
        newErrors.toAccountId = currentTransaction && currentTransaction.amount < 0 
          ? 'Please select a destination account' 
          : 'Please select a source account';
      }
      if (formData.accountId === formData.toAccountId) {
        newErrors.toAccountId = 'Source and destination accounts must be different';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add state for delete confirmation dialog
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle transaction deletion with core store
  const handleDelete = async () => {
    if (!user?.id) return;
    
    setIsDeleting(true);
    
    try {
      // Use the local transaction store to delete the transaction
      // This will update both the core store and the local state
      await transactionStore.deleteTransaction(currentTransaction.id);
      
      toast.success("Transaction Deleted", {
        description: "The transaction has been deleted successfully"
      });
      
      onClose();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error("Error", {
        description: "Failed to delete transaction. Please try again."
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async (formData: FormData) => {
    if (!user?.id) {
      toast.error("Error", {
        description: "You must be logged in to update a transaction"
      });
      return;
    }
    
    // Validate form before proceeding
    if (!validateForm()) return;
    
    setIsSubmitting(true);

    try {
      // First, verify that the transaction still exists in the database
      try {
        // Check if the transaction exists using a separate query
        const { data: existingTransaction, error: fetchError } = await supabase
          .from('transactions')
          .select('id')
          .eq('id', currentTransaction.id)
          .single();
          
        if (fetchError || !existingTransaction) {
          // Transaction doesn't exist anymore
          toast.error("Transaction Not Found", {
            description: "This transaction no longer exists in the database. It may have been deleted."
          });
          onClose?.();
          return;
        }
      } catch (verifyError) {
        console.error('Error verifying transaction existence:', verifyError);
        // Continue with the update attempt anyway
      }
      
      // Base transaction data with required fields
      const baseTransactionData = {
        type: formData.type as 'expense' | 'income' | 'transfer',
        amount: parseFloat(formData.amount),
        date: formData.date,
        description: formData.description.trim(),
        accountId: formData.accountId,
        status: formData.status || 'completed',
        updatedAt: new Date().toISOString(),
        userId: user.id
      };

      // Create a clean transaction data object with only defined values
      const transactionData = Object.entries(baseTransactionData).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc as Record<string, any>;
      }, {} as Record<string, any>);

      // Ensure userId is set (this is required for the store to work)
      transactionData.userId = user.id;

      // Add optional fields only if they have values
      if (formData.category) {
        transactionData.categoryId = formData.category;
        // Use the stored category name if available
        transactionData.category = formData.categoryName || '';
      }

      if (formData.payee?.id || formData.payee?.name) {
        // Store both payee ID and name for the transaction
        transactionData.payeeId = formData.payee.id || '';
        transactionData.payeeName = formData.payee.name || '';
      }

      if (formData.notes?.trim()) {
        transactionData.notes = formData.notes.trim();
      }

      if (Array.isArray(formData.tags) && formData.tags.length > 0) {
        transactionData.tags = formData.tags.filter(tag => tag !== undefined && tag !== null && tag !== '');
      }
      
      // Handle attachments - ensure we save the complete attachment objects
      if (currentTransaction?.metadata?.attachments) {
        transactionData.attachments = currentTransaction.metadata.attachments;
        transactionData.hasAttachment = currentTransaction.metadata.attachments.length > 0;
      }

      // Check if we're changing the transaction type
      const isNowTransfer = formData.type === 'transfer';
      const wasTransfer = currentTransaction?.type === 'transfer';
      
      // If we're changing to/from a transfer, we need special handling
      if ((isNowTransfer && !wasTransfer) || (!isNowTransfer && wasTransfer)) {
        try {
          if (isNowTransfer) {
            // Convert to transfer transaction
            try {
              // First, delete the existing transaction
              await coreTransactionStore.deleteTransaction(user.id, currentTransaction.id);
              
              // Get account information
              const fromAccount = accounts.find(a => a.id === formData.accountId);
              const toAccount = accounts.find(a => a.id === formData.toAccountId);
              
              if (!fromAccount || !toAccount) {
                throw new Error('Could not find specified accounts');
              }
              
              // Calculate the transfer amount and exchange rate
              const transferAmount = Math.abs(parseFloat(formData.amount));
              const exchangeRate = parseFloat(formData.exchangeRate.toString()) || 1;
              
              // Determine account direction based on original transaction type
              let finalFromAccountId, finalToAccountId;
              
              if (currentTransaction.amount < 0) {
                // Original was expense - current account is FROM, selected account is TO
                finalFromAccountId = formData.accountId;
                finalToAccountId = formData.toAccountId;
              } else {
                // Original was income - selected account is FROM, current account is TO
                finalFromAccountId = formData.toAccountId;
                finalToAccountId = formData.accountId;
              }
              
              console.log(`Converting to TRANSFER:
  - Source amount: ${transferAmount} ${fromAccount?.currency || 'USD'}
  - Exchange rate: ${exchangeRate}
  - From account: ${finalFromAccountId} (${fromAccount?.name || 'Unknown'})
  - To account: ${finalToAccountId} (${toAccount?.name || 'Unknown'})
`);
              
              // Use the proper createTransfer method (same as mobile app)
              await coreTransactionStore.createTransfer(user.id, {
                fromAccountId: finalFromAccountId,
                toAccountId: finalToAccountId,
                amount: transferAmount,
                exchangeRate: exchangeRate,
                date: formData.date,
                description: formData.description || '',
                notes: formData.notes || '',
                tags: formData.tags || []
              });
              
            } catch (error) {
              console.error('Error converting to transfer transaction:', error);
              throw error;
            }
          } else {
            // Convert from transfer to regular transaction
            try {
              // Delete the current transaction (the enhanced deleteTransaction method will handle linked transactions automatically)
              await coreTransactionStore.deleteTransaction(user.id, currentTransaction.id);
              
              // Create a new regular transaction
              const regularTransactionData = {
                type: formData.type as TransactionType,
                amount: formData.type === 'expense' ? -Math.abs(parseFloat(formData.amount)) : Math.abs(parseFloat(formData.amount)),
                account_id: formData.accountId,
                category_id: formData.category,
                date: formData.date,
                description: formData.description || '',
                notes: formData.notes || '',
                tags: formData.tags || [],
                status: formData.status || 'completed',
                payee_id: formData.payee?.id || '',
                payee_name: formData.payee?.name || '',
                attachments: transactionData.attachments || [],
                hasAttachment: transactionData.hasAttachment || false,
              };
              
              // Add the new regular transaction
              await coreTransactionStore.addTransaction(user.id, regularTransactionData as any);
              
            } catch (error) {
              console.error('Error converting from transfer transaction:', error);
              throw error;
            }
          }
          
          // Invalidate transactions query to refresh the list
          invalidateTransactions(queryClient);
          
          toast.success("Transaction Updated", {
            description: `Successfully converted transaction type to ${formData.type}`
          });
          
          onClose?.();
        } catch (error: any) {
          console.error('Error converting transaction type:', error);
          
          // Check for specific error types
          if (error?.code === 'PGRST116' || 
              (error?.message && error.message.includes('not found')) || 
              (error?.details && error.details.includes('0 rows'))) {
            toast.error("Transaction Not Found", {
              description: "This transaction no longer exists in the database. It may have been deleted."
            });
            onClose?.(); // Close the modal since we can't edit a non-existent transaction
          } else {
            toast.error("Error", {
              description: "Failed to convert transaction type. Please try again."
            });
          }
        }
      } else {
        // Regular update (not changing transaction type)
        try {
          // Use the web transaction store's updateTransaction method
          // This will call the core store AND update the web store's local state
          const success = await transactionStore.updateTransaction(currentTransaction.id, transactionData);
          
          if (success) {
            // Success toast already shown by the store method
            // Force refresh the transactions to ensure UI is updated
            try {
              console.log('Transaction updated successfully, refreshing transaction list...');
              if (user?.id) {
                await transactionStore.fetchTransactions(user.id, {
                  page: 1,
                  pageSize: 200,
                  orderBy: 'date',
                  orderDirection: 'desc'
                });
                console.log('Transaction list refreshed after update');
              }
            } catch (refreshError) {
              console.error('Error refreshing transactions after update:', refreshError);
            }
            
            onClose?.();
          } else {
            // Error already handled by the store method
            return;
          }
        } catch (error: any) {
          // Error already handled by the store method, but handle critical cases
          console.error('Error updating transaction:', error);
          
          // Only handle transaction not found case to close the modal
          if (error?.code === 'PGRST116' || 
              (error?.message && error.message.includes('not found')) || 
              (error?.details && error.details.includes('0 rows'))) {
            onClose?.(); // Close the modal since we can't edit a non-existent transaction
          }
        }
      }
    } catch (error) {
      console.error('Error in transaction update:', error);
      
      toast.error("Error", {
        description: "Failed to update transaction. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    console.log('Selected category ID:', categoryId);
    
    // Find the selected category to get its name
    const findCategoryName = (categories: Category[]): string => {
      for (const category of categories) {
        if (category.id === categoryId) {
          return category.name;
        }
        if (category.subcategories) {
          const subcategory = category.subcategories.find(sub => sub.id === categoryId);
          if (subcategory) {
            return `${category.name} > ${subcategory.name}`;
          }
        }
      }
      return '';
    };
    
    // Get the category name
    const categoryName = findCategoryName(categories);
    console.log('Found category name:', categoryName);
    
    // Update the form data with both ID and name
    handleInputChange('category', categoryId);
    
    // Store the category name for later use in the transaction
    setFormData(prev => ({
      ...prev,
      categoryName
    }));
    
    setIsCategoryOpen(false);
  }

  const handleAddNewPayee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    
    setIsAddingPayee(true);
    
    try {
      // Create new payee data
      const newPayeeData = {
        name: newPayeeName,
        user_id: user.id,
        type: formData.type || 'expense', // Add required type field
        category: formData.category || '',
        // Optional fields
        icon: '',
        color: '',
        notes: ''
      };
      
      // Add the payee
      // @ts-ignore - Type mismatch during migration
      await addPayee(newPayeeData);
      
      // Refresh payees list to get the newly added payee
      await fetchPayees(user.id);
      
      // Find the newly added payee by name
      const newPayee = payees.find(p => p.name === newPayeeName);
      
      if (newPayee) {
        // Update form data with the new payee
        handleInputChange('payee', {
          id: newPayee.id,
          name: newPayee.name
        });
        
        // Show success toast
        toast.success('Success', {
          description: `Payee "${newPayee.name}" added successfully`
        });
      } else {
        // Create a temporary payee object if not found in the list yet
        const tempPayee = {
          id: Date.now().toString(), // Use temporary ID
          name: newPayeeName
        };
        
        handleInputChange('payee', tempPayee);
        
        toast.success('Success', {
          description: 'Payee added successfully'
        });
      }
      
      // Reset state and close modal
      setNewPayeeName('');
      setIsAddPayeeModalOpen(false);
    } catch (error) {
      console.error('Error adding payee:', error);
      toast.error('Error', {
        description: 'Failed to add payee'
      });
    } finally {
      setIsAddingPayee(false);
    }
  };

  const handleAddNewTag = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    
    setIsAddingTag(true);
    
    try {
      // Create new tag data
      const newTagData = {
        user_id: user.id,
        name: newTagName,
        color: selectedColor
      };
      
      // Add the tag
      await addTag(newTagData);
      
      // Find the newly added tag by name
      const newTag = tags.find(t => t.name === newTagName);
      
      if (newTag) {
        // Update form data with the new tag
        setFormData({
          ...formData,
          tags: [...formData.tags, newTag.id]
        });
        
        // Show success toast
        toast.success('Success', {
          description: `Tag "${newTag.name}" added successfully`
        });
      } else {
        toast.success('Success', {
          description: 'Tag added successfully'
        });
      }
      
      // Reset state and close modal
      setNewTagName('');
      setSelectedColor('#6366F1');
      setIsAddTagModalOpen(false);
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error('Error', {
        description: 'Failed to add tag'
      });
    } finally {
      setIsAddingTag(false);
    }
  };

  const colorOptions = [
    { id: 'blue', name: 'Blue', value: '#3B82F6' },
    { id: 'indigo', name: 'Indigo', value: '#6366F1' },
    { id: 'purple', name: 'Purple', value: '#8B5CF6' },
    { id: 'pink', name: 'Pink', value: '#EC4899' },
    { id: 'red', name: 'Red', value: '#EF4444' },
    { id: 'orange', name: 'Orange', value: '#F97316' },
    { id: 'amber', name: 'Amber', value: '#F59E0B' },
    { id: 'yellow', name: 'Yellow', value: '#EAB308' },
    { id: 'lime', name: 'Lime', value: '#84CC16' },
    { id: 'green', name: 'Green', value: '#22C55E' },
    { id: 'emerald', name: 'Emerald', value: '#10B981' },
    { id: 'teal', name: 'Teal', value: '#14B8A6' },
    { id: 'cyan', name: 'Cyan', value: '#06B6D4' },
    { id: 'sky', name: 'Sky', value: '#0EA5E9' },
    { id: 'violet', name: 'Violet', value: '#7C3AED' },
    { id: 'fuchsia', name: 'Fuchsia', value: '#D946EF' },
    { id: 'rose', name: 'Rose', value: '#F43F5E' },
    { id: 'slate', name: 'Slate', value: '#64748B' },
  ]

  // Update handleAttachmentChange function to properly handle the update
  const handleAttachmentChange = (attachments: Attachment[]) => {
    console.log('Handling attachment change:', attachments);
    // Create a new array with properly structured attachments
    const updatedAttachments = attachments.map(attachment => ({
      id: attachment.id,
      name: attachment.name || attachment.fileName,
      type: attachment.type || attachment.fileType,
      url: attachment.url || attachment.fileUrl,
      fileName: attachment.fileName,
      fileUrl: attachment.fileUrl,
      fileType: attachment.fileType,
      uploadedAt: attachment.uploadedAt || new Date(),
      size: attachment.size || 0,
      userId: attachment.userId,
      createdAt: attachment.createdAt || new Date(),
      updatedAt: attachment.updatedAt || new Date()
    }));

    setFormData(prev => ({
      ...prev,
      attachments: updatedAttachments
    }));
  };

  // Handle cancel - reset form and close modal
  const handleCancel = () => {
    // If there are new attachments that were added but not saved, delete them
    const originalAttachments = currentTransaction?.metadata?.attachments || [];
    const currentAttachments = formData.attachments;
    
    // Find attachments that are in currentAttachments but not in originalAttachments
    const newAttachments = currentAttachments.filter(
      (attachment: Attachment) => !originalAttachments.some((orig: Attachment) => orig.id === attachment.id)
    );
    
    // Delete new attachments from storage if canceling
    if (newAttachments.length > 0 && user && currentTransaction?.id) {
      newAttachments.forEach(async (attachment) => {
        try {
          await deleteTransactionAttachment(user.id, currentTransaction.id, attachment);
        } catch (error) {
          console.error('Error deleting attachment:', error);
        }
      });
    }
    
    onClose();
  };

  const handlePayeeChange = (payee: FormPayee | null) => {
    console.log('Selected payee:', payee);
    
    // Update the form data with the payee information
    if (payee) {
      // If we have a payee with an ID but no name, try to find the name in the payees array
      if (payee.id && !payee.name && payees.length > 0) {
        const foundPayee = payees.find(p => p.id === payee.id);
        if (foundPayee) {
          payee.name = foundPayee.name;
        }
      }
    }
    
    handleInputChange('payee', payee || { id: '', name: '' });
    
    // Close the dropdown and clear search query when a payee is selected
    if (payee?.id) {
      setIsPayeeOpen(false);
      setPayeeQuery('');
    }
  }

  // Get currencies from the store at component level
  const { currencies } = useCurrenciesStore();

  // Transfer merge handlers
  // New function that accepts target account ID directly (like mobile version)
  const handleTransferTypeChangeWithAccount = async (targetAccountId: string) => {
    console.log('Transfer detection with target account:', targetAccountId);
    
    if (!currentTransaction || !user?.id) {
      console.log('Missing data for transfer detection:', {
        hasCurrentTransaction: !!currentTransaction,
        hasUser: !!user?.id
      });
      return;
    }

    // Smart transaction fetching for transfer detection
    let allTransactionsForDetection = transactions || [];
    
    // Check if we need to fetch additional transactions for old transactions
    const transactionDate = new Date(currentTransaction.date);
    const now = new Date();
    const daysDifference = (now.getTime() - transactionDate.getTime()) / (1000 * 60 * 60 * 24);
    
    try {
      
      console.log(`📅 Transaction is ${daysDifference.toFixed(1)} days old`);
      
      // If transaction is older than 30 days, fetch additional transactions from that time period
      if (daysDifference > 30) {
        console.log('🔍 Old transaction detected, fetching additional transactions from target date range...');
        
        // Calculate date range around the transaction (±7 days)
        const startDate = new Date(transactionDate);
        startDate.setDate(startDate.getDate() - 7);
        const endDate = new Date(transactionDate);
        endDate.setDate(endDate.getDate() + 7);
        
        console.log(`📅 Fetching transactions from ${startDate.toISOString()} to ${endDate.toISOString()}`);
        
        // Fetch transactions from the target date range
        const { data: additionalTransactions, error } = await supabase
          .from('transactions')
          .select('*')
          .eq('user_id', user.id)
          .gte('date', startDate.toISOString())
          .lte('date', endDate.toISOString())
          .neq('id', currentTransaction.id); // Exclude current transaction
        
        if (error) {
          console.error('Error fetching additional transactions:', error);
        } else if (additionalTransactions && additionalTransactions.length > 0) {
          console.log(`✅ Fetched ${additionalTransactions.length} additional transactions from target date range`);
          
          // Merge with existing transactions, avoiding duplicates
          const existingIds = new Set(allTransactionsForDetection.map(t => t.id));
          const newTransactions = additionalTransactions.filter(t => !existingIds.has(t.id));
          
          allTransactionsForDetection = [...allTransactionsForDetection, ...newTransactions];
          console.log(`📊 Total transactions for detection: ${allTransactionsForDetection.length} (${newTransactions.length} new)`);
        } else {
          console.log('ℹ️ No additional transactions found in target date range');
        }
        
        // If we have a target account, also fetch from that account in a wider range
        if (targetAccountId) {
          console.log(`🏦 Fetching additional transactions from target account: ${targetAccountId}`);
          
          const { data: accountTransactions, error: accountError } = await supabase
            .from('transactions')
            .select('*')
            .eq('user_id', user.id)
            .eq('account_id', targetAccountId)
            .gte('date', startDate.toISOString())
            .lte('date', endDate.toISOString())
            .neq('id', currentTransaction.id);
          
          if (accountError) {
            console.error('Error fetching account transactions:', accountError);
          } else if (accountTransactions && accountTransactions.length > 0) {
            console.log(`✅ Fetched ${accountTransactions.length} additional transactions from target account`);
            
            // Merge account transactions, avoiding duplicates
            const currentIds = new Set(allTransactionsForDetection.map(t => t.id));
            const newAccountTransactions = accountTransactions.filter(t => !currentIds.has(t.id));
            
            allTransactionsForDetection = [...allTransactionsForDetection, ...newAccountTransactions];
            console.log(`📊 Final total transactions for detection: ${allTransactionsForDetection.length} (${newAccountTransactions.length} from target account)`);
          }
        }
      }
    } catch (error) {
      console.error('Error in smart transaction fetching:', error);
      // Continue with existing transactions if fetching fails
    }

    // Find potential matches with the expanded transaction set
    const accountsData = accounts.map(acc => ({ id: acc.id, currency: acc.currency }));
    const matches = findPotentialTransferMatches(
      currentTransaction as any, 
      allTransactionsForDetection as any, 
      targetAccountId, 
      accountsData,
      {
        exchangeRate: formData.exchangeRate || 1.0
      }
    );
    
    if (matches.length > 0) {
      setPotentialMatches(matches);
      setShowTransferMergeModal(true);
    } else {
      
      // If no matches found with standard criteria and this is an old transaction, try relaxed detection
      if (daysDifference > 30) {
        console.log('🔍 Trying relaxed detection for old transaction...');
        
        // Import the relaxed function dynamically
        try {
          const { findPotentialTransferMatchesRelaxed } = await import('./utils/transferDetection');
          const relaxedMatches = findPotentialTransferMatchesRelaxed(
            currentTransaction as any, 
            allTransactionsForDetection as any, 
            targetAccountId,
            accountsData,
            {
              amountTolerancePercentage: 15.0, // 15% tolerance
              minimumConfidence: 50, // Lower threshold
              exchangeRate: formData.exchangeRate || 1.0
            }
          );
          
          if (relaxedMatches.length > 0) {
            console.log(`✅ Relaxed detection found ${relaxedMatches.length} matches!`);
            setPotentialMatches(relaxedMatches);
            setShowTransferMergeModal(true);
          } else {
            console.log('❌ No matches found even with relaxed criteria');
          }
        } catch (error) {
          console.error('Error with relaxed detection:', error);
        }
      }
    }
  };

  const handleTransferTypeChange = async () => {
    console.log('Transfer detection (legacy function)');
    
    if (!currentTransaction || !user?.id) {
      console.log('Missing data for transfer detection:', {
        hasCurrentTransaction: !!currentTransaction,
        hasUser: !!user?.id
      });
      return;
    }

    // Smart transaction fetching for transfer detection
    let allTransactionsForDetection = transactions || [];
    
    // Check if we need to fetch additional transactions for old transactions
    const transactionDate = new Date(currentTransaction.date);
    const now = new Date();
    const daysDifference = (now.getTime() - transactionDate.getTime()) / (1000 * 60 * 60 * 24);
    
    try {
      
      console.log(`📅 Transaction is ${daysDifference.toFixed(1)} days old`);
      
      // If transaction is older than 30 days, fetch additional transactions from that time period
      if (daysDifference > 30) {
        console.log('🔍 Old transaction detected, fetching additional transactions from target date range...');
        
        // Calculate date range around the transaction (±7 days)
        const startDate = new Date(transactionDate);
        startDate.setDate(startDate.getDate() - 7);
        const endDate = new Date(transactionDate);
        endDate.setDate(endDate.getDate() + 7);
        
        console.log(`📅 Fetching transactions from ${startDate.toISOString()} to ${endDate.toISOString()}`);
        
        // Fetch transactions from the target date range
        const { data: additionalTransactions, error } = await supabase
          .from('transactions')
          .select('*')
          .eq('user_id', user.id)
          .gte('date', startDate.toISOString())
          .lte('date', endDate.toISOString())
          .neq('id', currentTransaction.id); // Exclude current transaction
        
        if (error) {
          console.error('Error fetching additional transactions:', error);
        } else if (additionalTransactions && additionalTransactions.length > 0) {
          console.log(`✅ Fetched ${additionalTransactions.length} additional transactions from target date range`);
          
          // Merge with existing transactions, avoiding duplicates
          const existingIds = new Set(allTransactionsForDetection.map(t => t.id));
          const newTransactions = additionalTransactions.filter(t => !existingIds.has(t.id));
          
          allTransactionsForDetection = [...allTransactionsForDetection, ...newTransactions];
          console.log(`📊 Total transactions for detection: ${allTransactionsForDetection.length} (${newTransactions.length} new)`);
        } else {
          console.log('ℹ️ No additional transactions found in target date range');
        }
        
        // If we have a target account, also fetch from that account in a wider range
        if (formData.toAccountId) {
          console.log(`🏦 Fetching additional transactions from target account: ${formData.toAccountId}`);
          
          const { data: accountTransactions, error: accountError } = await supabase
            .from('transactions')
            .select('*')
            .eq('user_id', user.id)
            .eq('account_id', formData.toAccountId)
            .gte('date', startDate.toISOString())
            .lte('date', endDate.toISOString())
            .neq('id', currentTransaction.id);
          
          if (accountError) {
            console.error('Error fetching account transactions:', accountError);
          } else if (accountTransactions && accountTransactions.length > 0) {
            console.log(`✅ Fetched ${accountTransactions.length} additional transactions from target account`);
            
            // Merge account transactions, avoiding duplicates
            const currentIds = new Set(allTransactionsForDetection.map(t => t.id));
            const newAccountTransactions = accountTransactions.filter(t => !currentIds.has(t.id));
            
            allTransactionsForDetection = [...allTransactionsForDetection, ...newAccountTransactions];
            console.log(`📊 Final total transactions for detection: ${allTransactionsForDetection.length} (${newAccountTransactions.length} from target account)`);
          }
        }
      }
    } catch (error) {
      console.error('Error in smart transaction fetching:', error);
      // Continue with existing transactions if fetching fails
    }

    // Find potential matches with the expanded transaction set
    console.log('Looking for transfer matches...');
    console.log('Calling findPotentialTransferMatches with:', {
      targetTransaction: {
        id: currentTransaction.id,
        type: currentTransaction.type,
        amount: currentTransaction.amount,
        account_id: currentTransaction.account_id,
        date: currentTransaction.date
      },
      allTransactionsCount: allTransactionsForDetection.length,
      originalTransactionsCount: transactions?.length || 0
    });
    
    // Check if target account is selected before running detection
    if (!formData.toAccountId) {
      console.log('ℹ️ No target account selected yet - transfer detection will run when account is selected');
      return;
    }

    console.log('🔍 Running transfer detection with target account:', formData.toAccountId);
    const accountsData = accounts.map(acc => ({ id: acc.id, currency: acc.currency }));
    const matches = findPotentialTransferMatches(
      currentTransaction as any, 
      allTransactionsForDetection as any, 
      formData.toAccountId, 
      accountsData,
      {
        exchangeRate: formData.exchangeRate || 1.0
      }
    );
    console.log('Found matches:', matches);
    
    if (matches.length > 0) {
      console.log(`Found ${matches.length} potential transfer matches:`, matches);
      setPotentialMatches(matches);
      setShowTransferMergeModal(true);
    } else {
      console.log('No transfer matches found');
      
      // If no matches found with standard criteria and this is an old transaction, try relaxed detection
      if (daysDifference > 30) {
        console.log('🔍 Trying relaxed detection for old transaction...');
        
        // Import the relaxed function dynamically
        try {
          const { findPotentialTransferMatchesRelaxed } = await import('./utils/transferDetection');
          const relaxedMatches = findPotentialTransferMatchesRelaxed(
            currentTransaction as any, 
            allTransactionsForDetection as any, 
            formData.toAccountId,
            accountsData,
            {
              amountTolerancePercentage: 15.0, // 15% tolerance
              minimumConfidence: 50, // Lower threshold
              exchangeRate: formData.exchangeRate || 1.0
            }
          );
          
          if (relaxedMatches.length > 0) {
            console.log(`✅ Relaxed detection found ${relaxedMatches.length} matches!`);
            setPotentialMatches(relaxedMatches);
            setShowTransferMergeModal(true);
          } else {
            console.log('❌ No matches found even with relaxed criteria');
          }
        } catch (error) {
          console.error('Error with relaxed detection:', error);
        }
      }
    }
  };

  const handleConfirmMerge = async (sourceTransaction: any, matchingTransaction: any) => {
    try {
      console.log('Converting to TRANSFER:', {
        'Source amount': `${Math.abs(sourceTransaction.amount)} ${sourceTransaction.currency}`,
        'Target amount': `${Math.abs(matchingTransaction.amount)} ${matchingTransaction.currency}`,
        'From account': `${sourceTransaction.account_id}`,
        'To account': `${matchingTransaction.account_id}`
      });
      
      // Check if the convertToTransfer method exists
      if (typeof coreTransactionStore.convertToTransfer !== 'function') {
        console.error('convertToTransfer method not found on coreTransactionStore:', Object.keys(coreTransactionStore));
        toast.error('Update required', {
          description: 'Please refresh the page to enable transfer conversion.',
        });
        return;
      }
      
      console.log('Calling convertToTransfer method...');
      
      // Use the new convertToTransfer method to link existing transactions
      const result = await coreTransactionStore.convertToTransfer(user?.id!, sourceTransaction, matchingTransaction);
      
      console.log('convertToTransfer result:', result);
      
      // Refresh data
      invalidateTransactions(queryClient);
      
      setShowTransferMergeModal(false);
      setPotentialMatches([]);
      onClose();
      
      toast.success('Transfer created successfully', {
        description: 'The transactions have been converted to a transfer.',
      });
    } catch (error) {
      console.error('Error converting transactions to transfer:', error);
      toast.error('Error converting to transfer', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const handleCreateTransferOnly = () => {
    setShowTransferMergeModal(false);
    setPotentialMatches([]);
    // Continue with regular transfer creation - the type is already set to 'transfer'
  };

  const handleCloseMergeModal = () => {
    setShowTransferMergeModal(false);
    setPotentialMatches([]);
    // Revert type back to original
    handleInputChange('type', currentTransaction.type);
  };

  // Check if this is a transfer transaction
  const [isTransferTransaction, setIsTransferTransaction] = useState(false);
  const [transferId, setTransferId] = useState<string | null>(null);

  // Check if transaction is a transfer when the modal opens
  useEffect(() => {
    const checkIfTransfer = () => {
      if (!currentTransaction?.id || !user?.id) return;
      
      try {
        // Only use the TransferEditModal for transactions with the new transfer_id structure
        const hasTransferId = !!(currentTransaction as any).transfer_id;
        
        if (hasTransferId) {
          // This is a new transfer with transfer_id, use the transfer modal
          setIsTransferTransaction(true);
          setTransferId((currentTransaction as any).transfer_id);
        } else {
          // For legacy transfers or regular transactions, use the regular edit form
          // The regular edit form already handles legacy transfers
          setIsTransferTransaction(false);
          setTransferId(null);
        }
      } catch (error) {
        console.error('Error checking if transfer:', error);
        setIsTransferTransaction(false);
        setTransferId(null);
      }
    };

    if (isOpen && currentTransaction) {
      checkIfTransfer();
    }
  }, [isOpen, currentTransaction, user?.id]);

  // If this is a transfer transaction, render the TransferEditModal instead
  if (isTransferTransaction) {
    return (
      <TransferEditModal
        isOpen={isOpen}
        onClose={onClose}
        transferId={transferId || undefined}
        transactionId={!transferId ? currentTransaction.id : undefined}
        onSaved={() => {
          // Refresh transaction data after saving
          if (user?.id) {
            transactionStore.fetchTransactions(user.id);
          }
        }}
      />
    );
  }

  return (
    <>
    <StyledModal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Edit Transaction"
      maxWidth="900px"
      footer={
        <>
          <div className="flex justify-between gap-3 w-full">
            {/* Delete button on the left side */}
            <div>
              <StyledModalButton
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSubmitting || isDeleting}
                variant="danger"
              >
                Delete
              </StyledModalButton>
            </div>
            
            <div className="flex gap-3">
              <StyledModalButton
                onClick={handleCancel}
                variant="outline"
                disabled={isSubmitting}
              >
                Cancel
              </StyledModalButton>
              <StyledModalButton
                onClick={() => {
                  const form = document.getElementById('editTransactionForm') as HTMLFormElement;
                  if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                }}
                disabled={isSubmitting}
                variant="primary"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </StyledModalButton>
            </div>
          </div>
        </>
      }
    >
      <div className="space-y-3">
        <form id="editTransactionForm" onSubmit={(e) => { e.preventDefault(); handleSubmit(formData); }} className="space-y-3">
          {/* Transaction Type Selector */}
          <div className="flex gap-3 p-1 bg-[#F3F4F6] dark:bg-[#1E2B42]/50 rounded-xl">
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); handleInputChange('type', 'expense'); }}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                formData.type === 'expense'
                  ? 'bg-[#E53935] text-white shadow-sm'
                  : 'text-[#2F2F2F] dark:text-[#A3A3A3] hover:bg-white/50 dark:hover:bg-[#1E2B42]/50'
              }`}
              disabled={isSubmitting}
            >
              Expense
            </button>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); handleInputChange('type', 'income'); }}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                formData.type === 'income'
                  ? 'bg-[#439476] text-white shadow-sm'
                  : 'text-[#2F2F2F] dark:text-[#A3A3A3] hover:bg-white/50 dark:hover:bg-[#1E2B42]/50'
              }`}
              disabled={isSubmitting}
            >
              Income
            </button>
            <button
              type="button"
              onClick={() => {
                handleInputChange('type', 'transfer');
              }}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                formData.type === 'transfer'
                  ? 'bg-[#439476] text-white shadow-sm'
                  : 'text-[#2F2F2F] dark:text-[#A3A3A3] hover:bg-white/50 dark:hover:bg-[#1E2B42]/50'
              }`}
              disabled={isSubmitting}
            >
              Transfer
            </button>
          </div>

          {/* Main Form Fields with Improved Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Amount Field */}
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                Amount
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-text-secondary dark:text-text-dark-secondary sm:text-sm">{getCurrencySymbol(accounts, formData.accountId)}</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  className={`block w-full pl-7 pr-3 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ${
                    errors.amount ? 'ring-error' : 'ring-border dark:ring-border-dark'
                  } placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors h-10 text-left rtl:text-right`}
                  placeholder="0.00"
                  required
                  disabled={isSubmitting}
                />
                {errors.amount && (
                  <p className="mt-1 text-xs text-error">{errors.amount}</p>
                )}
              </div>
            </div>

            {/* Date Field */}
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                Date
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                </div>
                <input
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  className={`block w-full pl-10 pr-3 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ${
                    errors.date ? 'ring-error' : 'ring-border dark:ring-border-dark'
                  } placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors h-10 text-left rtl:text-right`}
                  required
                  disabled={isSubmitting}
                />
                {errors.date && (
                  <p className="mt-1 text-xs text-error">{errors.date}</p>
                )}
              </div>
            </div>

            {/* Account Field */}
            <div className="md:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                {formData.type === 'transfer' 
                  ? (currentTransaction && currentTransaction.amount < 0 ? 'From Account' : 'To Account')
                  : 'Account'
                }
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CreditCard className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                </div>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                </div>
                <select
                  value={formData.accountId}
                  onChange={(e) => handleInputChange('accountId', e.target.value)}
                  className="block w-full pl-10 pr-10 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ring-border dark:ring-border-dark focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors appearance-none h-10 text-left rtl:text-right"
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select account</option>
                  {Array.isArray(accounts) && accounts.sort((a, b) => a.name.localeCompare(b.name)).map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
                {errors.accountId && (
                  <p className="mt-1 text-xs text-error">{errors.accountId}</p>
                )}
              </div>
            </div>
          </div>

          {/* Description Field - Full Width */}
          <div>
            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
              Description (optional)
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="block w-full px-3 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ring-border dark:ring-border-dark placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors h-10 text-left rtl:text-right"
              placeholder="Enter description (optional)"
              disabled={isSubmitting}
            />
          </div>

          {/* Transfer Fields Section */}
          {formData.type === 'transfer' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Conditionally render From/To account fields based on original transaction type */}
              {currentTransaction && currentTransaction.amount < 0 ? (
                // Original was expense (negative) - current account is FROM, select TO account
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                    To Account
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CreditCard className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                    </div>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                    </div>
                    <select
                      value={formData.toAccountId}
                      onChange={(e) => handleInputChange('toAccountId', e.target.value)}
                      className="block w-full pl-10 pr-10 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ring-border dark:ring-border-dark focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors appearance-none h-10 text-left rtl:text-right"
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">Select destination account</option>
                      {Array.isArray(accounts) && accounts
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .filter(account => account.id !== formData.accountId)
                        .map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name}
                          </option>
                        ))}
                    </select>
                    {errors.toAccountId && (
                      <p className="mt-1 text-xs text-error">{errors.toAccountId}</p>
                    )}
                  </div>
                </div>
              ) : (
                // Original was income (positive) - current account is TO, select FROM account  
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                    From Account
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CreditCard className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                    </div>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                    </div>
                    <select
                      value={formData.toAccountId}
                      onChange={(e) => handleInputChange('toAccountId', e.target.value)}
                      className="block w-full pl-10 pr-10 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ring-border dark:ring-border-dark focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors appearance-none h-10 text-left rtl:text-right"
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">Select source account</option>
                      {Array.isArray(accounts) && accounts
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .filter(account => account.id !== formData.accountId)
                        .map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name}
                          </option>
                        ))}
                    </select>
                    {errors.toAccountId && (
                      <p className="mt-1 text-xs text-error">{errors.toAccountId}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Exchange Rate Input - Show when both accounts are selected */}
              {formData.accountId && formData.toAccountId && (
                <div>
                  <ExchangeRateInput
                    fromAccountId={formData.accountId}
                    toAccountId={formData.toAccountId}
                    exchangeRate={parseFloat(formData.exchangeRate.toString())}
                    onExchangeRateChange={(rate) => handleInputChange('exchangeRate', rate)}
                    onFetchLatestRate={async () => {
                      try {
                        if (!user?.id) {
                          toast.error("Error", {
                            description: "User not authenticated"
                          });
                          return;
                        }
                        
                        // Get the accounts to determine their currencies
                        const fromAccount = accounts.find(acc => acc.id === formData.accountId);
                        const toAccount = accounts.find(acc => acc.id === formData.toAccountId);
                        
                        // Get currency information from accounts
                        const fromAccountCurrencyId = (fromAccount as any)?.currencyId;
                        const toAccountCurrencyId = (toAccount as any)?.currencyId;
                        const fromAccountCurrencyCode = fromAccount?.currency;
                        const toAccountCurrencyCode = toAccount?.currency;
                        
                        if (!fromAccountCurrencyCode || !toAccountCurrencyCode) {
                          toast.error("Error", {
                            description: "Currency information is missing for one or both accounts"
                          });
                          return;
                        }

                        // List of currencies that might not be supported by the exchange rate API
                        const unsupportedCurrencies = ['IQD', 'IRR', 'SYP', 'VES'];
                        
                        // Check if we're dealing with unsupported currencies
                        if (unsupportedCurrencies.includes(fromAccountCurrencyCode) || 
                            unsupportedCurrencies.includes(toAccountCurrencyCode)) {
                          
                          // Use fallback rates for common pairs
                          let rate = 1;
                          
                          if (fromAccountCurrencyCode === 'USD' && toAccountCurrencyCode === 'IQD') {
                            rate = 1309.5; // Approximate USD to IQD rate
                          } else if (fromAccountCurrencyCode === 'IQD' && toAccountCurrencyCode === 'USD') {
                            rate = 0.00076; // Approximate IQD to USD rate
                          } else {
                            // For other unsupported currency pairs, show a warning
                            toast.error("Warning", {
                              description: `Automatic rates for ${fromAccountCurrencyCode} to ${toAccountCurrencyCode} are not available. Please enter manually.`
                            });
                            return;
                          }
                          
                          // Round to 4 decimal places
                          rate = Math.round(rate * 10000) / 10000;
                          
                          handleInputChange('exchangeRate', rate);
                          
                          toast.success("Note", {
                            description: `Using approximate exchange rate: 1 ${fromAccountCurrencyCode} = ${rate} ${toAccountCurrencyCode}`
                          });
                          return;
                        }

                        // Fetch latest rates if not available
                        const { fetchLatestRates, convertCurrency } = await import('@/services/exchangeRatesService');
                        const exchangeRates = await fetchLatestRates();

                        try {
                          // Use currencies from component scope instead of calling the hook here
                          const fromCurrency = currencies.find((c: any) => 
                            c.id === fromAccountCurrencyId || 
                            c.code === fromAccountCurrencyCode
                          );
                          
                          const toCurrency = currencies.find((c: any) => 
                            c.id === toAccountCurrencyId || 
                            c.code === toAccountCurrencyCode
                          );

                          // Use currency codes for conversion
                          const fromCurrencyCode = fromCurrency?.code || fromAccountCurrencyCode;
                          const toCurrencyCode = toCurrency?.code || toAccountCurrencyCode;
                          
                          if (!fromCurrencyCode || !toCurrencyCode) {
                            toast.error("Error", {
                              description: "Could not determine currencies for the accounts"
                            });
                            return;
                          }

                          // Calculate the exchange rate using core store (supports precious metals)
                          let rate: number;
                          try {
                            rate = coreConvertAmount(1, fromCurrencyCode, toCurrencyCode);
                          } catch (error) {
                            // Fallback to the legacy service
                            rate = convertCurrency(1, fromCurrencyCode, toCurrencyCode, exchangeRates?.rates || {});
                          }

                          if (!rate || isNaN(rate)) {
                            toast.error("Error", {
                              description: "Could not calculate exchange rate"
                            });
                            return;
                          }

                          // Round to 6 decimal places for precious metals
                          const roundedRate = Math.round(rate * 1000000) / 1000000;
                          
                          handleInputChange('exchangeRate', roundedRate);
                          
                          // Show proper units in the toast
                          const preciousMetals = ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'];
                          const fromUnit = preciousMetals.includes(fromCurrencyCode) ? `${fromCurrencyCode}/g` : fromCurrencyCode;
                          const toUnit = preciousMetals.includes(toCurrencyCode) ? `${toCurrencyCode}/g` : toCurrencyCode;
                          
                          toast.success("Success", {
                            description: `Exchange rate updated: 1 ${fromUnit} = ${roundedRate} ${toUnit}`
                          });
                        } catch (error) {
                          console.error('Error calculating exchange rate:', error);
                          toast.error("Error", {
                            description: "Failed to calculate exchange rate. Please enter it manually."
                          });
                        }
                      } catch (error) {
                        console.error('Error updating exchange rate:', error);
                        toast.error("Error", {
                          description: "Failed to update exchange rate"
                        });
                      }
                    }}
                    className="mt-2"
                  />
                </div>
              )}
            </div>
          )}

          {/* Only show category and payee fields for non-transfer transactions */}
          {formData.type !== 'transfer' && (
            <>
              {/* Category Field */}
              <div className="col-span-full">
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2">
                  Category
                </label>
                <div className="relative flex-1">
                  <Combobox
                    as="div"
                    value={formData.category || ''}
                    onChange={handleCategoryChange}
                    className="relative"
                  >
                    <div className="relative">
                      <div
                        className="flex items-center bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark rounded-xl px-3 h-11 cursor-pointer hover:border-secondary dark:hover:border-primary-dark transition-colors"
                        onClick={handleCategoryOpen}
                      >
                        <Tag className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary mr-2" />
                        <Combobox.Input
                          className="bg-transparent flex-1 outline-none border-none rounded-none focus:ring-0 focus:outline-none shadow-none focus:shadow-none text-text-primary dark:text-text-dark-primary placeholder-text-secondary dark:placeholder-text-dark-secondary appearance-none"
                          placeholder="Select category..."
                          onChange={(e) => setCategoryQuery(e.target.value)}
                          onBlur={() => setTimeout(() => setIsCategoryOpen(false), 200)}
                          displayValue={(categoryId: string) => {
                            if (!categoryId) return '';
                            const findCategory = (categories: Category[]) => {
                              for (const category of categories) {
                                if (category.id === categoryId) {
                                  return category.name;
                                }
                                if (category.subcategories) {
                                  const subcategory = category.subcategories.find(sub => sub.id === categoryId);
                                  if (subcategory) {
                                    return `${category.name} → ${subcategory.name}`;
                                  }
                                }
                              }
                              return '';
                            };
                            return findCategory(categories);
                          }}
                        />
                        <ChevronDown className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary ml-2" />
                      </div>

                      <Transition
                        show={isCategoryOpen}
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Combobox.Options
                          static
                          className="absolute z-[70] mt-1 max-h-60 w-full overflow-auto rounded-xl bg-background-secondary dark:bg-background-dark-secondary py-1 shadow-lg ring-1 ring-border dark:ring-border-dark focus:outline-none text-sm backdrop-blur-xl backdrop-saturate-150"
                        >
                          {filteredCategories.length === 0 && categoryQuery !== '' ? (
                            <div className="relative cursor-default select-none py-2 px-4 text-text-secondary dark:text-text-dark-secondary">
                              Nothing found.
                            </div>
                          ) : (
                            filteredCategories.map((category) => (
                              <Fragment key={category.id}>
                                {/* Parent Category */}
                                <Combobox.Option
                                  value={category.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-4 pr-9 ${
                                      active
                                        ? 'bg-secondary/10 text-secondary dark:bg-primary-dark/10 dark:text-primary-dark'
                                        : 'text-text-primary dark:text-text-dark-primary'
                                    }`
                                  }
                                >
                                  {({ selected, active }) => (
                                    <>
                                      <div className="flex items-center">
                                        <div
                                          className="w-2 h-2 rounded-full mr-2"
                                          style={{ backgroundColor: category.color }}
                                        />
                                        <span className={`block truncate ${selected ? 'font-bold' : ''}`}>
                                          {category.name}
                                        </span>
                                      </div>
                                      {selected && (
                                        <span
                                          className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                                            active ? 'text-white' : 'text-secondary dark:text-primary-dark'
                                          }`}
                                        >
                                          <Check className="h-4 w-4" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>

                                {/* Subcategories */}
                                {category.subcategories?.map((subcategory: { id: string; name: string }) => (
                                  <Combobox.Option
                                    key={subcategory.id}
                                    value={subcategory.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-8 pr-9 ${
                                        active
                                          ? 'bg-secondary/10 text-secondary dark:bg-primary-dark/10 dark:text-primary-dark'
                                          : 'text-text-primary dark:text-text-dark-primary'
                                      }`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <div className="flex items-center">
                                          <div
                                            className="w-1.5 h-1.5 rounded-full mr-2"
                                            style={{ backgroundColor: category.color }}
                                          />
                                          <span className={`block truncate ${selected ? 'font-bold' : ''}`}>
                                            {subcategory.name}
                                          </span>
                                        </div>
                                        {selected && (
                                          <span
                                            className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                                              active ? 'text-secondary dark:text-primary-dark' : 'text-secondary dark:text-primary-dark'
                                            }`}
                                          >
                                            <Check className="h-4 w-4" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Fragment>
                            ))
                          )}
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                </div>
              </div>

              {/* Payee Field */}
              <div className="col-span-full">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    Payee
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsAddPayeeModalOpen(true)}
                    className="inline-flex items-center text-xs text-secondary hover:text-secondary/90 dark:text-primary-dark dark:hover:text-primary-dark/90"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add New
                  </button>
                </div>
                <div className="relative flex-1 payee-dropdown-container">
                  <div className="relative">
                    <div
                      className="flex items-center bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark rounded-xl px-3 h-11 cursor-pointer hover:border-secondary dark:hover:border-primary-dark transition-colors"
                      onClick={handlePayeeOpen}
                    >
                      <User className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary mr-2" />
                      <input
                        type="text"
                        className="bg-transparent flex-1 outline-none border-none rounded-none focus:ring-0 focus:outline-none shadow-none focus:shadow-none text-text-primary dark:text-text-dark-primary placeholder-text-secondary dark:placeholder-text-dark-secondary appearance-none"
                        placeholder="Select payee..."
                        value={payeeQuery || formData.payee?.name || ''}
                        onChange={(e) => {
                          setPayeeQuery(e.target.value);
                          setIsPayeeOpen(true);
                          // Clear the selected payee when typing
                          handlePayeeChange({ id: '', name: e.target.value });
                        }}
                        onFocus={() => setIsPayeeOpen(true)}
                      />
                      <ChevronDown className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary ml-2" />
                    </div>

                    {isPayeeOpen && (
                      <div className="absolute z-[9999] w-full bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark rounded-xl mt-1 py-1 text-base shadow-lg max-h-60 overflow-auto focus:outline-none sm:text-sm backdrop-blur-xl backdrop-saturate-150">
                        {filteredPayees.length === 0 && payeeQuery !== '' ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-text-secondary dark:text-text-dark-secondary">
                            Nothing found.
                          </div>
                        ) : (
                          filteredPayees.map((payee) => (
                            <div
                              key={payee.id}
                              className={`relative cursor-pointer select-none py-2 pl-4 pr-9 hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark ${
                                formData.payee?.id === payee.id
                                  ? 'bg-secondary/10 text-secondary dark:bg-primary-dark/10 dark:text-primary-dark'
                                  : 'text-text-primary dark:text-text-dark-primary'
                              }`}
                              onClick={() => handlePayeeChange({ id: payee.id, name: payee.name })}
                            >
                              <div className="flex items-center">
                                <span className={`block truncate ${formData.payee?.id === payee.id ? 'font-bold' : ''}`}>
                                  {payee.name}
                                </span>
                              </div>
                              {formData.payee?.id === payee.id && (
                                <span
                                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-secondary dark:text-primary-dark"
                                >
                                  <Check className="h-4 w-4" aria-hidden="true" />
                                </span>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {errors.payee && (
                  <p className="mt-1 text-xs text-negative dark:text-negative-dark">{errors.payee}</p>
                )}
              </div>
            </>
          )}

          {/* Optional Fields */}
          <div className="col-span-full grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Notes Field */}
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5">
                Notes (optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="block w-full px-3 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ring-border dark:ring-border-dark placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-input dark:bg-background-dark-input transition-colors"
                placeholder="Add any additional notes"
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            {/* Tags Field */}
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  Tags
                </label>
                <button
                  type="button"
                  onClick={() => setIsAddTagModalOpen(true)}
                  className="inline-flex items-center text-xs text-secondary hover:text-secondary/90 dark:text-primary-dark dark:hover:text-primary-dark/90"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add New
                </button>
              </div>
              <TagSelect
                value={formData.tags}
                onChange={(tags) => setFormData({ ...formData, tags })}
              />
            </div>
          </div>

          {/* Attachments Section */}
          <div className="col-span-full">
            <label className="block text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8] mb-1.5">
              Attachments
            </label>
            {user && currentTransaction?.id && (
              <AttachmentUpload
                userId={user.id}
                transactionId={currentTransaction.id}
                attachments={formData.attachments}
                onAttachmentChange={handleAttachmentChange}
                disabled={isSubmitting}
              />
            )}
          </div>
        </form>
      </div>
    </StyledModal>

    {/* Add Delete Confirmation Dialog */}
    <StyledModal
      isOpen={showDeleteConfirm}
      onClose={() => !isDeleting && setShowDeleteConfirm(false)}
      title="Delete Transaction"
      maxWidth="400px"
      footer={
        <>
          <StyledModalButton
            onClick={() => setShowDeleteConfirm(false)}
            variant="outline"
            disabled={isDeleting}
          >
            Cancel
          </StyledModalButton>
          <StyledModalButton
            onClick={handleDelete}
            variant="danger"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </StyledModalButton>
        </>
      }
    >
      <p className="text-text-primary dark:text-text-dark-primary">
        Are you sure you want to delete this transaction? This action cannot be undone.
      </p>
    </StyledModal>

    {/* Add New Payee Modal */}
    <StyledModal
      isOpen={isAddPayeeModalOpen}
      onClose={() => setIsAddPayeeModalOpen(false)}
      title="Add New Payee"
      maxWidth="400px"
      footer={
        <>
          <StyledModalButton
            onClick={() => setIsAddPayeeModalOpen(false)}
            variant="outline"
            disabled={isAddingPayee}
          >
            Cancel
          </StyledModalButton>
          <StyledModalButton
            onClick={() => {
              const form = document.getElementById('addPayeeForm') as HTMLFormElement;
              if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            }}
            variant="primary"
            disabled={isAddingPayee || !newPayeeName.trim()}
          >
            {isAddingPayee ? 'Adding...' : 'Add Payee'}
          </StyledModalButton>
        </>
      }
    >
      <form id="addPayeeForm" onSubmit={handleAddNewPayee}>
        <div>
          <label htmlFor="payeeName" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2">
            Payee Name
          </label>
          <input
            id="payeeName"
            type="text"
            value={newPayeeName}
            onChange={(e) => setNewPayeeName(e.target.value)}
            className="h-11 w-full px-3 py-2.5 text-text-primary dark:text-text-dark-primary bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark rounded-xl focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary"
            placeholder="Enter payee name"
            required
            disabled={isAddingPayee}
          />
        </div>
      </form>
    </StyledModal>

    {/* Add New Tag Modal */}
    <StyledModal
      isOpen={isAddTagModalOpen}
      onClose={() => {
        setIsAddTagModalOpen(false);
        setNewTagName('');
        setSelectedColor(colorOptions[0].value);
      }}
      title="Add Tag"
      maxWidth="400px"
      footer={
        <>
          <StyledModalButton
            onClick={() => {
              setIsAddTagModalOpen(false);
              setNewTagName('');
              setSelectedColor(colorOptions[0].value);
            }}
            variant="outline"
            disabled={isAddingTag}
          >
            Cancel
          </StyledModalButton>
          <StyledModalButton
            onClick={() => {
              const form = document.getElementById('addTagForm') as HTMLFormElement;
              if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            }}
            variant="primary"
            disabled={isAddingTag || !newTagName.trim()}
          >
            {isAddingTag ? 'Adding...' : 'Add Tag'}
          </StyledModalButton>
        </>
      }
    >
      <form id="addTagForm" onSubmit={handleAddNewTag} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
            Name
          </label>
          <input
            type="text"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            className="h-11 w-full px-3 py-2.5 text-text-primary dark:text-text-dark-primary bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark rounded-xl focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2">
            Color
          </label>
          <div className="grid grid-cols-6 gap-2">
            {colorOptions.map((color) => (
              <button
                key={color.id}
                type="button"
                onClick={() => setSelectedColor(color.value)}
                className="relative w-8 h-8 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary dark:focus:ring-primary-dark"
                style={{ backgroundColor: color.value }}
              >
                {selectedColor === color.value && (
                  <Check className="absolute inset-0 m-auto w-4 h-4 text-white" />
                )}
              </button>
            ))}
          </div>
          <div className="mt-2 flex items-center space-x-2">
            <div
              className="w-6 h-6 rounded-full border border-border dark:border-border-dark"
              style={{ backgroundColor: selectedColor }}
            />
            <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
              {colorOptions.find(c => c.value === selectedColor)?.name}
            </span>
          </div>
        </div>
      </form>
    </StyledModal>

      {/* Transfer Merge Modal */}
      {currentTransaction && (
        <TransferMergeModal
          isOpen={showTransferMergeModal}
          onClose={handleCloseMergeModal}
          sourceTransaction={currentTransaction as any}
          potentialMatches={potentialMatches}
          onConfirmMerge={handleConfirmMerge}
          onCreateTransferOnly={handleCreateTransferOnly}
        />
      )}
    </>
  );
}

